import { useEffect, useState, useCallback } from "react";
import { useAppDispatch } from "hooks";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Checkbox,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import {
  createContact,
  updateContact,
  updateContactStatus,
  fetchContactById,
} from "services/contacts.service";
import Loading from "components/loading/Loading";
import { validateEmail } from "common/validator";

function ContactForm({ customer, id }: { customer: string; id?: string }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [viber, setViber] = useState("");
  const [isAuthorizedToSign, setIsAuthorizedToSign] = useState(false);
  const [groundsForRepresentation, setGroundsForRepresentation] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const fetchData = useCallback(
    async (id: string) => {
      setIsLoading(true);
      const data = await fetchContactById(companyId, customer, id);
      setName(data.name);
      setPosition(data.position);
      setPhone(data.phone);
      setEmail(data.email);
      setLinkedin(data.linkedin || "");
      setWhatsapp(data.whatsapp || "");
      setViber(data.viber || "");
      setIsAuthorizedToSign(data.isAuthorizedToSign);
      setGroundsForRepresentation(data.groundsForRepresentation);
      setIsDisabled(!!data.isDisabled);
      setIsLoading(false);
    },
    [companyId, customer]
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  if (!companyId) return null;

  const handleFormSubmit = async () => {
    const baseFormData = {
      id,
      name,
      position,
      phone,
      email,
      linkedin: linkedin || null,
      whatsapp: whatsapp || null,
      viber: viber || null,
      groundsForRepresentation,
    };
    
    // Include status fields only when creating (not updating)
    const formData = !id
      ? {
          ...baseFormData,
          isAuthorizedToSign,
          isDisabled,
        }
      : baseFormData;
    
    const action = id ? updateContact : createContact;
    try {
      setIsLoading(true);
      await action(formData, customer, companyId);
      dispatch(fetchCustomer({ customerId: customer, companyId }));
      setSuccessMessage("ok");
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (field: "isAuthorizedToSign" | "isDisabled", value: boolean) => {
    // Only update status if contact already exists
    if (!id || isUpdatingStatus) {
      return;
    }

    try {
      setIsUpdatingStatus(true);
      setErrMsg(""); // Clear previous errors
      await updateContactStatus(id, customer, companyId, {
        [field]: value,
      });
      // Refresh customer data in background to keep store in sync
      // This won't reset form state since form uses local state and only refetches on id change
      dispatch(fetchCustomer({ customerId: customer, companyId }));
      setIsUpdatingStatus(false);
    } catch (e: any) {
      console.error(e);
      setIsUpdatingStatus(false);
      
      // Extract error message from API response
      let errorMessage = "error";
      if (e?.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e?.response?.data?.message) {
        errorMessage = e.response.data.message;
      }
      
      setErrMsg(errorMessage);
      // Revert the checkbox state on error
      if (field === "isAuthorizedToSign") {
        setIsAuthorizedToSign(!value);
      } else {
        setIsDisabled(!value);
      }
    }
  };

  const handleAuthorizedToSignChange = (value: boolean) => {
    if (!id) return; // Don't allow status changes for new contacts
    setIsAuthorizedToSign(value);
    handleStatusUpdate("isAuthorizedToSign", value);
  };

  const handleDisabledChange = (value: boolean) => {
    if (!id) return; // Don't allow status changes for new contacts
    setIsDisabled(value);
    handleStatusUpdate("isDisabled", value);
  };

  if (isLoading && !name) {
    return <Loading />;
  }

  const handleEmailChange = (value: string) => {
    setEmail(value);
    const validationError = validateEmail(value)
      ? ""
      : t("invalid_email_address");
    setError(validationError ? t(validationError) : "");
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div className="">
              <Banner tone="critical">{t(errMsg)}</Banner>
            </div>
          )}
          {successMessage && (
            <div className="">
              <Banner tone="info">{t(successMessage)}</Banner>
            </div>
          )}
        </div>
        <TextField
          label={t("contact-name")}
          id="name"
          name="name"
          value={name}
          onChange={setName}
          autoComplete="off"
        />
        <TextField
          label={t("position")}
          id="position"
          name="position"
          value={position}
          onChange={setPosition}
          autoComplete="off"
        />
        <TextField
          label={t("phone")}
          id="phone"
          name="phone"
          value={phone}
          onChange={setPhone}
          autoComplete="off"
        />
        <TextField
          label={t("email")}
          id="email"
          name="email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          autoComplete="off"
          error={error}
        />
        <TextField
          label={t("linkedin", { defaultValue: "LinkedIn" })}
          id="linkedin"
          name="linkedin"
          value={linkedin}
          onChange={setLinkedin}
          autoComplete="off"
          placeholder="https://linkedin.com/in/…"
        />
        <TextField
          label={t("whatsapp", { defaultValue: "WhatsApp" })}
          id="whatsapp"
          name="whatsapp"
          value={whatsapp}
          onChange={setWhatsapp}
          autoComplete="off"
          placeholder="+370…"
        />
        <TextField
          label={t("viber", { defaultValue: "Viber" })}
          id="viber"
          name="viber"
          value={viber}
          onChange={setViber}
          autoComplete="off"
          placeholder="+370…"
        />
        <TextField
          label={t("grounds_for_representation")}
          id="groundsForRepresentation"
          name="groundsForRepresentation"
          type="text"
          value={groundsForRepresentation}
          onChange={setGroundsForRepresentation}
          autoComplete="off"
        />
        <Checkbox
          label={t("is-authorized-to-sign")}
          checked={isAuthorizedToSign}
          onChange={id ? handleAuthorizedToSignChange : setIsAuthorizedToSign}
        />
        <Checkbox
          label={t("contact_disabled")}
          checked={isDisabled}
          onChange={id ? handleDisabledChange : setIsDisabled}
        />
        <Button
          onClick={handleFormSubmit}
          disabled={!name || !validateEmail(email) || isUpdatingStatus}
          loading={isLoading || isUpdatingStatus}
        >
          {t("save")}
        </Button>
      </FormLayout>
    </Form>
  );
}

export default ContactForm;
